/**
 * Mobile Navigation Components
 *
 * All mobile navigation components including buttons and dropdowns
 */

import React from "react";
import { useTranslation } from "react-i18next";
import NavigationStylesExport from "../NavigationStyles";
import { NavigationLink } from "../NavigationStyles";
import LanguageSwitcher from "../LanguageSwitcher";
import { MobileButtons } from "./mobile/MobileButtons";
import {
  MobileProductsDropdown,
  MobileMagazineDropdown,
  MobileBusinessDropdown,
  MobileCompanyDropdown,
  MobileAIServicesDropdown,
} from "./mobile/MobileDropdowns";

// Export MobileButtons for use in Header
export { MobileButtons };

// Mobile Simple Link Component (for Home and News)
const MobileSimpleLink = ({ to, label, closeMobileMenu }) => {
  return (
    <NavigationLink
      to={to}
      replace={true}
      variant="mobile-link"
      onClick={closeMobileMenu}
    >
      <div className="flex items-center justify-between">
        <span>{label}</span>
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </div>
    </NavigationLink>
  );
};

// Complete Mobile Navigation Menu
export const MobileNavigation = ({
  isMobileMenuOpen,
  productItems,
  magazineItems,
  businessItems,
  companyItems,
  aiServicesItems,
  dropdownStates,
  setDropdownStates,
  closeMobileMenu,
}) => {
  const { HEADER_STYLES } = NavigationStylesExport;
  const { t } = useTranslation("common");

  if (!isMobileMenuOpen) return null;

  return (
    <div className={HEADER_STYLES.mobileMenu}>
      <div className={HEADER_STYLES.mobileMenuContent}>
        {/* Home Link */}
        <MobileSimpleLink
          to="/"
          label={t("navigation.home")}
          closeMobileMenu={closeMobileMenu}
        />

        <MobileProductsDropdown
          productItems={productItems}
          isOpen={dropdownStates.products}
          setIsOpen={(isOpen) =>
            setDropdownStates((prev) => ({ ...prev, products: isOpen }))
          }
          closeMobileMenu={closeMobileMenu}
        />

        <MobileMagazineDropdown
          magazineItems={magazineItems}
          isOpen={dropdownStates.magazine}
          setIsOpen={(isOpen) =>
            setDropdownStates((prev) => ({ ...prev, magazine: isOpen }))
          }
          closeMobileMenu={closeMobileMenu}
        />

        <MobileBusinessDropdown
          businessItems={businessItems}
          isOpen={dropdownStates.business}
          setIsOpen={(isOpen) =>
            setDropdownStates((prev) => ({ ...prev, business: isOpen }))
          }
          closeMobileMenu={closeMobileMenu}
        />

        <MobileAIServicesDropdown
          aiServicesItems={aiServicesItems}
          isOpen={dropdownStates.aiServices}
          setIsOpen={(isOpen) =>
            setDropdownStates((prev) => ({ ...prev, aiServices: isOpen }))
          }
          closeMobileMenu={closeMobileMenu}
        />

        <MobileCompanyDropdown
          companyItems={companyItems}
          isOpen={dropdownStates.company}
          setIsOpen={(isOpen) =>
            setDropdownStates((prev) => ({ ...prev, company: isOpen }))
          }
          closeMobileMenu={closeMobileMenu}
        />

        {/* News Link */}
        <MobileSimpleLink
          to="/news"
          label={t("navigation.news")}
          closeMobileMenu={closeMobileMenu}
        />

        {/* Language Switcher */}
        <div className="mt-4 pt-4 border-t border-gray-700/50">
          <LanguageSwitcher variant="mobile" />
        </div>
      </div>
    </div>
  );
};
